export interface DailyRecapRow {
  key: string;
  label: string;
  value: string;
  span?: number;
}

export interface DailyRecapSection {
  key: string;
  title: string;
  description: string;
  tone: "blue" | "green" | "amber" | "red" | "violet";
  rows: DailyRecapRow[];
}

export function formatDailyRecapDate(value: unknown): string {
  if (!value) return "la journée concernée";

  const rawValue = String(value);
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(rawValue)
    ? `${rawValue}T12:00:00`
    : rawValue;
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) return rawValue;

  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function formatDailyRecapShortDate(value: unknown): string {
  if (!value) return "la journée concernée";

  const rawValue = String(value);
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(rawValue)
    ? `${rawValue}T12:00:00`
    : rawValue;
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) return rawValue;

  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
  }).format(date);
}

const MONEY_FIELDS = [
  ["total_sales", "Chiffre d’affaires"],
  ["total_payments_in", "Encaissements reçus"],
  ["total_purchases", "Achats"],
  ["total_payments_out", "Décaissements effectués"],
  ["net_cash_flow", "Solde net de trésorerie"],
  ["total_expenses", "Dépenses"],
  ["total_returns_sales", "Retours sur ventes"],
  ["total_returns_purchases", "Retours sur achats"],
] as const;

const COUNT_FIELDS = [
  ["sales_count", "Ventes enregistrées"],
  ["purchase_count", "Achats enregistrés"],
  ["items_sold", "Quantité totale vendue"],
  ["items_purchased", "Quantité totale achetée"],
] as const;

const RESTAURANT_FIELDS = [
  ["prepared_items_count", "Plats / produits préparés", "count"],
  ["production_cost", "Coût total de production", "money"],
  ["remaining_prepared_items_count", "Invendus / restants", "count"],
  ["remaining_prepared_items_cost", "Coût des invendus / restants", "money"],
  ["unsold_value_rate", "Taux d’invendus en valeur", "percent"],
  ["actual_margin", "Marge réelle sur ventes tracées", "money"],
  ["actual_margin_rate", "Taux de marge réelle", "percent"],
  [
    "actual_margin_coverage_percent",
    "Couverture des coûts réels",
    "percent",
  ],
  ["actual_margin_note", "Marge réelle", "text"],
  ["unpaid_sales_amount", "Ventes non encaissées", "money"],
  ["unpaid_sales_count", "Ventes à encaisser", "count"],
  ["open_anomaly_count", "Catégories d’anomalies ouvertes", "count"],
  ["open_anomalies_note", "Anomalies ouvertes", "text"],
  [
    "production_recommendation_note",
    "Production recommandée demain",
    "text",
  ],
] as const;

const asNumber = (value: unknown) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const displayCurrency = (currency: unknown) =>
  String(currency || "FCFA").toUpperCase() === "XOF"
    ? "FCFA"
    : String(currency || "FCFA").toUpperCase();

const asRecordArray = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value)
    ? value.filter(
        (item): item is Record<string, unknown> =>
          Boolean(item) && typeof item === "object" && !Array.isArray(item)
      )
    : [];

const formatPercent = (value: unknown) =>
  `${asNumber(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })} %`;

const formatMoney = (value: unknown, currency: string) =>
  `${asNumber(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })} ${currency}`;

const formatQuantity = (value: unknown) =>
  asNumber(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

export function getDailyRecapRows(data: Record<string, unknown>): DailyRecapRow[] {
  const presentRestaurantFields = RESTAURANT_FIELDS.filter(([key]) =>
    Object.prototype.hasOwnProperty.call(data, key)
  );
  const activityFields = [
    ...MONEY_FIELDS,
    ...COUNT_FIELDS,
    ...presentRestaurantFields,
  ];
  const mostProfitableDishes = asRecordArray(data.most_profitable_dishes);
  const dishesToWatch = asRecordArray(data.dishes_to_watch);
  const openAnomalies = asRecordArray(data.open_anomalies);
  const productionRecommendations = asRecordArray(
    data.production_recommendations
  );
  const hasActivity =
    activityFields.some(([key]) => asNumber(data[key]) !== 0) ||
    mostProfitableDishes.length > 0 ||
    dishesToWatch.length > 0 ||
    openAnomalies.length > 0 ||
    productionRecommendations.length > 0;

  if (!hasActivity) return [];

  const currency = displayCurrency(data.currency);
  const moneyRows = MONEY_FIELDS.map(([key, label]) => ({
    key,
    label,
    value: formatMoney(data[key], currency),
  }));
  const countRows = COUNT_FIELDS.map(([key, label]) => ({
    key,
    label,
    value: formatQuantity(data[key]),
  }));
  const restaurantRows = presentRestaurantFields.map(([key, label, type]) => {
    let value = formatQuantity(data[key]);
    if (type === "money") {
      value = formatMoney(data[key], currency);
    } else if (type === "percent") {
      value = formatPercent(data[key]);
    } else if (type === "text") {
      value = String(data[key] || "");
    }
    return { key, label, value, span: type === "text" ? 2 : undefined };
  });
  const profitableDishRows = mostProfitableDishes.map((dish, index) => ({
    key: `most_profitable_dish_${index}`,
    label: `Plat rentable n°${index + 1}`,
    value: `${String(dish.product_name || "Plat")} — ${formatMoney(
      dish.actual_margin,
      currency
    )}${
      dish.actual_margin_rate !== null &&
      dish.actual_margin_rate !== undefined
        ? ` (${formatPercent(dish.actual_margin_rate)})`
        : ""
    }`,
    span: 2,
  }));
  const watchedDishRows = dishesToWatch.map((dish, index) => ({
    key: `dish_to_watch_${index}`,
    label: `Plat à surveiller n°${index + 1}`,
    value: `${String(dish.product_name || "Plat")} — ${formatQuantity(
      dish.available_output_quantity
    )} ${String(
      dish.unit_short_name || "unité(s)"
    )} restant(s), ${formatMoney(dish.available_output_cost, currency)}`,
    span: 2,
  }));
  const anomalyRows = openAnomalies.map((anomaly, index) => ({
    key: `open_anomaly_${index}`,
    label: `Anomalie ouverte n°${index + 1}`,
    value: `${String(anomaly.title || "Anomalie à traiter")}${
      anomaly.impact_label ? ` — ${String(anomaly.impact_label)}` : ""
    }`,
    span: 2,
  }));
  const recommendationRows = productionRecommendations.map(
    (recommendation, index) => ({
      key: `production_recommendation_${index}`,
      label: `Production demain n°${index + 1}`,
      value: String(recommendation.message || "Recommandation indisponible"),
      span: 2,
    })
  );

  return [
    ...moneyRows,
    ...countRows,
    ...restaurantRows,
    ...profitableDishRows,
    ...watchedDishRows,
    ...anomalyRows,
    ...recommendationRows,
  ];
}

const SECTION_DEFINITIONS = [
  {
    key: "sales",
    title: "Ventes",
    description: "Activité commerciale et quantités vendues",
    tone: "blue" as const,
    keys: ["total_sales", "sales_count", "items_sold", "total_returns_sales"],
    prefixes: [],
  },
  {
    key: "cash",
    title: "Trésorerie",
    description: "Encaissements, décaissements et montants à recevoir",
    tone: "green" as const,
    keys: [
      "total_payments_in",
      "total_payments_out",
      "net_cash_flow",
      "total_expenses",
      "unpaid_sales_amount",
      "unpaid_sales_count",
    ],
    prefixes: [],
  },
  {
    key: "purchases",
    title: "Achats et approvisionnement",
    description: "Achats enregistrés et quantités approvisionnées",
    tone: "amber" as const,
    keys: [
      "total_purchases",
      "purchase_count",
      "items_purchased",
      "total_returns_purchases",
    ],
    prefixes: [],
  },
  {
    key: "production",
    title: "Production et rentabilité",
    description: "Production, invendus et marge réellement observée",
    tone: "violet" as const,
    keys: [
      "prepared_items_count",
      "production_cost",
      "remaining_prepared_items_count",
      "remaining_prepared_items_cost",
      "unsold_value_rate",
      "actual_margin",
      "actual_margin_rate",
      "actual_margin_coverage_percent",
      "actual_margin_note",
    ],
    prefixes: ["most_profitable_dish_", "dish_to_watch_"],
  },
  {
    key: "attention",
    title: "Points d’attention",
    description: "Anomalies à traiter et recommandations pour demain",
    tone: "red" as const,
    keys: [
      "open_anomaly_count",
      "open_anomalies_note",
      "production_recommendation_note",
    ],
    prefixes: ["open_anomaly_", "production_recommendation_"],
  },
] as const;

export function getDailyRecapSections(
  data: Record<string, unknown>
): DailyRecapSection[] {
  const rows = getDailyRecapRows(data);

  return SECTION_DEFINITIONS.map((section) => ({
    key: section.key,
    title: section.title,
    description: section.description,
    tone: section.tone,
    rows: rows.filter(
      (row) =>
        (section.keys as readonly string[]).includes(row.key) ||
        section.prefixes.some((prefix) => row.key.startsWith(prefix))
    ),
  })).filter((section) => section.rows.length > 0);
}
